import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { Integraciones, RespuestaAjustes, ValoresAjustes } from '../api/modelos'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { AvisoError, CabeceraPagina, Campo, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { SoloAdmin } from '../componentes/SoloAdmin'
import { useAjustes } from '../hooks/useCatalogo'
import { useIntegraciones } from '../hooks/useIntegraciones'
import { cambios, formDeValores, validarAjustes, valoresDeForm, type FormAjustes } from './ajustes/formAjustes'

function Seccion({ titulo, children, descripcion }: { titulo: string; descripcion?: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4">
      <legend className="px-1 text-sm font-semibold text-tinta">{titulo}</legend>
      {descripcion ? <p className="-mt-1 text-xs text-tinta-apagada">{descripcion}</p> : null}
      {children}
    </fieldset>
  )
}

function Casilla({ etiqueta, marcada, onCambiar }: { etiqueta: string; marcada: boolean; onCambiar: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-tinta-2">
      <input type="checkbox" checked={marcada} onChange={(e) => onCambiar(e.target.checked)} />
      {etiqueta}
    </label>
  )
}

function EstadoIntegraciones({ datos }: { datos?: Integraciones }) {
  if (!datos) return null
  const filas: [string, boolean, string][] = [
    ['WhatsApp', datos.whatsapp.configurado, `modo ${datos.whatsapp.modo}`],
    ['Correo (SMTP)', datos.correo.activo && datos.correo.configurado, datos.correo.configurado ? (datos.correo.activo ? 'activo' : 'apagado') : 'sin servidor SMTP'],
    ['Geocodificación', datos.geocodificacion.activa, datos.geocodificacion.activa ? 'activa' : 'apagada'],
    // RF-39: Holded solo se nombra cuando su copia está activa.
    ...(datos.holded.activo ? [['Holded', true, 'copia activa'] as [string, boolean, string]] : []),
    ['n8n', datos.n8n.configurado, datos.n8n.configurado ? 'configurado' : 'sin configurar'],
  ]
  return (
    <ul aria-label="Estado de las integraciones" className="flex flex-wrap gap-2">
      {filas.map(([nombre, ok, detalle]) => (
        <li key={nombre}>
          <Insignia tono={ok ? 'positivo' : 'neutro'}>{`${nombre}: ${detalle}`}</Insignia>
        </li>
      ))}
    </ul>
  )
}

function FormularioAjustes({ valores }: { valores: ValoresAjustes }) {
  const api = useApi()
  const qc = useQueryClient()
  const integraciones = useIntegraciones()
  const [f, setF] = useState<FormAjustes>(() => formDeValores(valores))
  const [errores, setErrores] = useState<string[]>([])
  const guardar = useMutation<{ ok: boolean; valores: ValoresAjustes }, ErrorApi, ValoresAjustes>({
    mutationFn: (cuerpo) => api.pedir('/api/ajustes', { metodo: 'POST', cuerpo }),
    onSuccess: (r) => {
      qc.setQueryData<RespuestaAjustes>(['ajustes'], (a) => ({ ...(a ?? {}), valores: r.valores }))
      void qc.invalidateQueries({ queryKey: ['integraciones'] })
    },
  })
  const poner = <K extends keyof FormAjustes>(k: K, v: FormAjustes[K]) => setF((a) => ({ ...a, [k]: v }))
  const texto = (k: keyof FormAjustes) => ({ value: String(f[k]), onChange: (e: { target: { value: string } }) => poner(k, e.target.value as never), className: CLASE_INPUT })

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    const encontrados = validarAjustes(f)
    setErrores(encontrados)
    if (encontrados.length) return
    const cuerpo = cambios(valores, valoresDeForm(f))
    if (Object.keys(cuerpo).length) guardar.mutate(cuerpo)
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <EstadoIntegraciones datos={integraciones.data} />

      <Seccion titulo="WhatsApp" descripcion="En modo manual el panel prepara el texto y el enlace wa.me; el servidor no envía nada.">
        <Campo etiqueta="Modo del canal">
          <select value={f.whatsapp_modo} onChange={(e) => poner('whatsapp_modo', e.target.value as FormAjustes['whatsapp_modo'])} className={CLASE_INPUT}>
            <option value="manual">Manual (wa.me)</option>
            <option value="n8n">n8n</option>
            <option value="ycloud">YCloud</option>
          </select>
        </Campo>
        <Campo etiqueta="Prefijo de país" ayuda="Se antepone a los móviles sin prefijo en los enlaces wa.me. Solo dígitos, p. ej. 34.">
          <input {...texto('whatsapp_prefijo_pais')} inputMode="numeric" maxLength={4} />
        </Campo>
      </Seccion>

      <Seccion titulo="Correo" descripcion="El servidor SMTP (host, usuario y contraseña) se fija con variables de entorno del backend y no se muestra aquí.">
        <Casilla etiqueta="Enviar correos" marcada={f.correo_activo} onCambiar={(v) => poner('correo_activo', v)} />
        <div className="grid gap-3 md:grid-cols-3">
          <Campo etiqueta="Destinatarios de pedidos" ayuda="Uno por línea o separados por comas."><textarea rows={3} {...texto('pedidos')} /></Campo>
          <Campo etiqueta="Destinatarios de rutas"><textarea rows={3} {...texto('rutas')} /></Campo>
          <Campo etiqueta="Destinatarios de incidencias"><textarea rows={3} {...texto('incidencias')} /></Campo>
        </div>
      </Seccion>

      <Seccion titulo="Geocodificación" descripcion="Apagada, las coordenadas se escriben a mano en la ficha del cliente y no se llama a ningún servicio.">
        <Casilla etiqueta="Geocodificación activa" marcada={f.geocodificacion_activa} onCambiar={(v) => poner('geocodificacion_activa', v)} />
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="URL del servicio"><input {...texto('geocodificacion_url')} /></Campo>
          <Campo etiqueta="Región" ayuda="Se añade a cada búsqueda, p. ej. «Madrid, España». Vacía = nada."><input {...texto('geocodificacion_region')} /></Campo>
        </div>
      </Seccion>

      <Seccion titulo="Rutas de reparto">
        <div className="grid gap-3 md:grid-cols-4">
          <Campo etiqueta="Rutas por día"><input {...texto('rutas_por_dia')} inputMode="numeric" /></Campo>
          <Campo etiqueta="Salida: latitud"><input {...texto('salida_latitud')} inputMode="decimal" /></Campo>
          <Campo etiqueta="Salida: longitud"><input {...texto('salida_longitud')} inputMode="decimal" /></Campo>
          <Campo etiqueta="Salida: nombre" ayuda="P. ej. «Almacén»."><input {...texto('salida_etiqueta')} /></Campo>
        </div>
      </Seccion>

      <Seccion titulo="Pedido" descripcion="Productos que acompañan a cada bono: «<nombre> <litros>». Vacío = el bono no lleva esa línea.">
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="Producto envase"><input {...texto('pedido_producto_envase')} /></Campo>
          <Campo etiqueta="Producto devolución de envase"><input {...texto('pedido_producto_devolucion')} /></Campo>
        </div>
      </Seccion>

      {errores.length ? (
        <ul role="alert" className="list-disc rounded-lg border border-atencion-anillo bg-atencion-fondo py-2 pl-8 pr-3 text-sm text-atencion-tinta">
          {errores.map((m) => <li key={m}>{m}</li>)}
        </ul>
      ) : null}
      <AvisoError error={guardar.error} />
      {guardar.isSuccess ? <p role="status" className="text-sm text-positivo-tinta">Ajustes guardados.</p> : null}
      <div>
        <button type="submit" disabled={guardar.isPending} className={CLASE_BOTON_PRIMARIO}>
          {guardar.isPending ? 'Guardando' : 'Guardar ajustes'}
        </button>
      </div>
    </form>
  )
}

function PantallaAjustes() {
  const ajustes = useAjustes()
  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina titulo="Ajustes" descripcion="Cómo funciona esta instalación sin servicios externos: WhatsApp, correo, geocodificación y rutas." />
      {ajustes.isPending ? (
        <Cargando mensaje="Cargando los ajustes" />
      ) : ajustes.isError ? (
        <EstadoError error={ajustes.error} onReintentar={() => void ajustes.refetch()} />
      ) : (
        <FormularioAjustes valores={ajustes.data.valores} />
      )}
    </div>
  )
}

/** Ajustes (RF-47), solo para administradores. */
export function Ajustes() {
  return (
    <SoloAdmin>
      <PantallaAjustes />
    </SoloAdmin>
  )
}
